import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { ArrayNotEmpty, IsNotEmpty, Matches } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { toast } from 'react-toastify';
import { inject } from 'tsyringe';

import type {
  AccessDto,
  AccountDto,
  IPegasusIOGateway,
  RoleDto,
  ScopeDto,
  TagDto,
} from '@/shared/application/interfaces/pegasus-io.gateway';
import { ObservableForm } from '@/shared/presentation/forms/BaseForm';

import { AddRoleToAccountCollaboration } from '../../application/collaborations/add-role-to-account.collaboration';
import { GetRolesCollaboration } from '../../application/collaborations/get-roles.collaboration';
import { GetScopesCollaboration } from '../../application/collaborations/get-scopes.collaboration';
import { GetUserCollaboration } from '../../application/collaborations/get-user.collaboration';
import { ImpersonateCollaboration } from '../../application/collaborations/impersonate.collaboration';
import { RemoveRoleFromAccountCollaboration } from '../../application/collaborations/remove-role-from-account.collaboration';

class AccessListForm {
  @IsNotEmpty({ message: 'Campo obrigatório' })
  role: RoleDto;

  @ArrayNotEmpty({ message: 'Campo obrigatório' })
  scopes: ScopeDto[] = [];
}

class TagForm {
  @IsNotEmpty({ message: 'Campo obrigatório' })
  @Matches(/^[a-z0-9-]+$/, {
    message: 'O nome da tag deve conter apenas letras minúsculas, números e hífens s',
  })
  name: string;

  @ArrayNotEmpty({ message: 'Campo obrigatório' })
  scopes: ScopeDto[];
}

@ViewModel({ singleton: true })
export class UserViewModel implements IReactiveModel {
  @observable accessListForm = new ObservableForm(AccessListForm);
  @observable tagForm = new ObservableForm(TagForm);

  @observable user: AccountDto;

  @observable accessList: AccessDto[] = [];
  @observable roles: RoleDto[] = [];
  @observable scopes: ScopeDto[] = [];
  @observable tags: TagDto[] = [];

  @observable isLoading = false;
  @observable hasError = false;
  @observable isImpersonating = false;

  @observable isAddingRole = false;
  @observable isAddingTag = false;
  @observable isRemovingAccessList = false;
  @observable isRemovingTag = false;

  @observable isAddRoleModalOpen = false;
  @observable isAddTagModalOpen = false;

  constructor(
    @inject('IPegasusIOGateway')
    private readonly pegasusGateway: IPegasusIOGateway,
    private getUserCollaboration: GetUserCollaboration,
    private getRolesCollaboration: GetRolesCollaboration,
    private getScopes: GetScopesCollaboration,
    private addRoleToAccountCollaboration: AddRoleToAccountCollaboration,
    private removeRoleFromAccountCollaboration: RemoveRoleFromAccountCollaboration,
    private impersonateCollaboration: ImpersonateCollaboration,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit(props) {
    try {
      this.isLoading = true;

      const [{ data: user }, { data: roles }, { data: scopes }, { data: tags }] = await Promise.all(
        [
          this.getUserCollaboration.run(props.registrationCode),
          this.getRolesCollaboration.run(),
          this.getScopes.run(),
          this.pegasusGateway.getAccountTags(props.registrationCode),
        ],
      );

      this.user = user;
      this.accessList = user.accessList;
      this.roles = roles;
      this.scopes = scopes;
      this.tags = tags;
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async addRoleToUser() {
    try {
      this.isAddingRole = true;

      const { data: accessAdded } = await this.addRoleToAccountCollaboration.run({
        scopeGuids: this.accessListForm.values.scopes.map((scope) => scope.guid),
        accountRegistrationCode: this.user.registrationCode,
        roleGuid: this.accessListForm.values.role.guid,
      });

      toast.success('Papel adicionado com sucesso');

      this.accessList = accessAdded.accessList;

      this.accessListForm.reset();
      this.closeAddRoleModal();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao adicionar papel');
    } finally {
      this.isAddingRole = false;
    }
  }

  @action
  async removeRoleFromAccount(newAccess: { role: RoleDto; scope: ScopeDto }) {
    try {
      this.isRemovingAccessList = true;

      await this.removeRoleFromAccountCollaboration.run({
        accountGuid: this.user.registrationCode,
        roleGuid: newAccess.role.guid,
        scopeGuid: newAccess.scope.guid,
      });

      toast.success('Papel removido com sucesso');

      this.accessList = this.accessList.filter(
        (access) =>
          access.role.guid !== newAccess.role.guid || access.scope.guid !== newAccess.scope.guid,
      );
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover papel');
    } finally {
      this.isRemovingAccessList = false;
    }
  }

  @action
  async addTagToAccount() {
    try {
      this.isAddingTag = true;

      if (!this.tagForm.isValid) {
        return;
      }

      const { data: newTag } = await this.pegasusGateway.addTagToAccount({
        tagName: this.tagForm.values.name,
        accountRegistrationCode: this.user.registrationCode,
        scopeGuid: this.tagForm.values.scopes[0].guid,
      });

      toast.success('Tag adicionada com sucesso');

      this.tags = [...this.tags, newTag];

      this.tagForm.reset();
      this.closeAddTagModal();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao adicionar tag');
    } finally {
      this.isAddingTag = false;
    }
  }

  @action
  async removeTagFromAccount(tag: TagDto) {
    try {
      await this.pegasusGateway.removeTagFromAccount({
        tagName: tag.name,
        accountRegistrationCode: this.user.registrationCode,
        scopeGuid: tag.scope.guid,
      });

      toast.success('Tag removida com sucesso');

      this.tags = this.tags.filter((t) => t.name !== tag.name || t.scope.guid !== tag.scope.guid);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover papel');
    } finally {
      this.isRemovingTag = false;
    }
  }

  @action
  openAddRoleModal() {
    this.isAddRoleModalOpen = true;
  }

  @action
  closeAddRoleModal() {
    this.isAddRoleModalOpen = false;
  }

  @action
  openAddTagModal() {
    this.isAddTagModalOpen = true;
  }

  @action
  closeAddTagModal() {
    this.isAddTagModalOpen = false;
  }

  @action
  async impersonate() {
    const result = confirm('Deseja realmente personificar esta conta?');

    if (result) {
      try {
        this.isImpersonating = true;
        await this.impersonateCollaboration.run({
          registrationCode: this.user.registrationCode,
        });

        window.location.href = '/';
      } catch (error) {
        console.error(error);

        if (error.response?.data?.identifier === 'Forbidden') {
          toast.error('Você não tem permissão para personificar esta conta.');
          return;
        }

        toast.error('Erro ao personificar conta. Tentar novamente mais tarde.');
      } finally {
        this.isImpersonating = false;
      }
    }
  }
}
